import React, { useState } from 'react';

import { Modal } from 'src/components/Modal/Modal';
import { Input } from 'src/components/Input';
import { Button } from 'src/components/Button';
import { Container } from 'src/components/Container';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  CampaignAudience,
  NOTIFICATION_LIMITS,
  campaignErrorMessage,
  createCampaign,
  describeAudience,
  previewCampaignAudience,
} from 'src/api/adminNotifications';

import AudiencePicker from './AudiencePicker';
import { Callout, CharCount, CheckboxRow, Field, FieldFooter, FieldLabel, FormGrid, Textarea } from './styled';

type NewCampaignModalProps = {
  onClose: () => void;
  onCreated: () => void;
};

/**
 * Compose → Preview (the API counts the audience) → Send. Sending only
 * queues; the worker delivers within a minute and the list shows progress.
 */
const NewCampaignModal: React.FC<NewCampaignModalProps> = ({ onClose, onCreated }) => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [description, setDescription] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [audience, setAudience] = useState<CampaignAudience>({ type: 'all' });
  const [pickedUsers, setPickedUsers] = useState<{ id: string; label: string }[]>([]);
  const [preview, setPreview] = useState<{ targeted: number; audience: string } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  const titleOk = title.trim().length > 0 && title.length <= NOTIFICATION_LIMITS.title;
  const messageOk = message.trim().length > 0 && message.length <= NOTIFICATION_LIMITS.message;
  const descriptionOk = description.length <= NOTIFICATION_LIMITS.description;
  const audienceOk =
    audience.type === 'all' ||
    (audience.type === 'country' && (audience.country_codes?.length ?? 0) > 0) ||
    (audience.type === 'kyc' && ((audience.kyc_statuses?.length ?? 0) > 0 || Boolean(audience.kyc_in_progress))) ||
    (audience.type === 'users' && (audience.user_ids?.length ?? 0) > 0);
  const canPreview = titleOk && messageOk && descriptionOk && audienceOk;

  // Any change to the audience invalidates the count shown.
  const updateAudience = (next: CampaignAudience) => {
    setAudience(next);
    setPreview(null);
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const result = await previewCampaignAudience(audience);
      setPreview({ targeted: result.targeted, audience: describeAudience(audience) });
    } catch (e: any) {
      dispatch(setAppAlert({ message: campaignErrorMessage(e, 'Could not count the audience'), isSuccess: false }));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!preview) return;
    setSending(true);
    try {
      const campaign = await createCampaign({
        title: title.trim(),
        message: message.trim(),
        description: description.trim() || undefined,
        channels: { email: sendEmail },
        audience,
      });
      dispatch(
        setAppAlert({
          message: `Campaign queued for ${campaign.counts.targeted} user${campaign.counts.targeted === 1 ? '' : 's'}`,
          isSuccess: true,
        }),
      );
      onCreated();
    } catch (e: any) {
      dispatch(setAppAlert({ message: campaignErrorMessage(e, 'Failed to create the campaign'), isSuccess: false }));
    } finally {
      setSending(false);
    }
  };

  const footer = (
    <Container display="flex" gap="0.75rem" justifyContent="flex-end" width="100%">
      <Button text="Cancel" className="outline" onClick={onClose} />
      {preview ? (
        <Button
          text={sending ? 'Sending…' : `Send to ${preview.targeted} user${preview.targeted === 1 ? '' : 's'}`}
          className="black"
          disabled={sending || preview.targeted === 0}
          onClick={handleSend}
        />
      ) : (
        <Button
          text={previewing ? 'Counting…' : 'Preview audience'}
          className="black"
          disabled={!canPreview || previewing}
          onClick={handlePreview}
        />
      )}
    </Container>
  );

  return (
    <Modal isOpen onClose={onClose} footer={footer}>
      <Container fontWeight={600} fontSize="1.25rem" paddingBottom="1rem">
        New notification campaign
      </Container>
      <FormGrid>
        <Field>
          <Input label="Title" placeholder="Scheduled maintenance" value={title} onChange={(e) => setTitle(e.target.value)} />
          <FieldFooter>
            <span />
            <CharCount $over={title.length > NOTIFICATION_LIMITS.title}>
              {title.length}/{NOTIFICATION_LIMITS.title}
            </CharCount>
          </FieldFooter>
        </Field>
        <Field>
          <FieldLabel>Message</FieldLabel>
          <Textarea placeholder="What the user sees in the notification" value={message} onChange={(e) => setMessage(e.target.value)} />
          <FieldFooter>
            <span />
            <CharCount $over={message.length > NOTIFICATION_LIMITS.message}>
              {message.length}/{NOTIFICATION_LIMITS.message}
            </CharCount>
          </FieldFooter>
        </Field>
        <Field>
          <FieldLabel>Details (optional)</FieldLabel>
          <Textarea placeholder="Longer text shown when the notification is opened" value={description} onChange={(e) => setDescription(e.target.value)} />
          <FieldFooter>
            <span />
            <CharCount $over={description.length > NOTIFICATION_LIMITS.description}>
              {description.length}/{NOTIFICATION_LIMITS.description}
            </CharCount>
          </FieldFooter>
        </Field>
        <CheckboxRow>
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
          Also send as an email (in-app and push always go out)
        </CheckboxRow>

        <AudiencePicker
          value={audience}
          onChange={updateAudience}
          pickedUsers={pickedUsers}
          onPickedUsersChange={setPickedUsers}
        />

        {preview ? (
          <Callout $tone={preview.targeted === 0 ? 'warning' : 'info'}>
            {preview.targeted === 0
              ? 'This audience matches no users. Adjust it and preview again.'
              : `This will reach ${preview.targeted} user${preview.targeted === 1 ? '' : 's'} (${preview.audience}). Delivery starts within a minute and cannot be recalled once sent.`}
          </Callout>
        ) : (
          <Callout>Preview counts the audience before anything is sent.</Callout>
        )}
      </FormGrid>
    </Modal>
  );
};

export default NewCampaignModal;
